import React from 'react'
import { NorthStarPayload } from '../../services/northstar'

// Hand-rolled inline SVG rather than a charting dependency: the app ships no
// chart library today, and one internal page is a poor reason to add one to
// every learner's bundle.
//
// Stacked COLUMNS, not a stacked area, and that is deliberate. A channel's band
// is null in any week it was not tracked, and an area chart has to invent a
// polygon edge across those gaps — which is exactly how a channel switching on
// gets drawn as revenue that was never earned. Columns simply omit the segment.
// Fill for a week with no channel breakdown. Exported so the legend can name
// it — an unexplained extra colour on the chart reads as a fourth channel.
export const UNATTRIBUTED_FILL = '#2c3455'

const PAD = { top: 12, right: 12, bottom: 22, left: 34 }
const W = 1040
const H = 260

const NorthStarChart: React.FC<{ data: NorthStarPayload }> = ({ data }) => {
  const rows = data.rows
  if (!rows.length) return null

  const plotW = W - PAD.left - PAD.right
  const plotH = H - PAD.top - PAD.bottom

  const maxIndex = Math.max(...rows.map((r) => r.index), 100)
  // Round the axis up to a clean number so the gridlines read as values, not
  // as wherever the data happened to stop.
  const step = maxIndex <= 200 ? 50 : maxIndex <= 500 ? 100 : 250
  const top = Math.ceil(maxIndex / step) * step
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH

  const slot = plotW / rows.length
  const barW = Math.max(1, slot * 0.72)

  const ticks: number[] = []
  for (let v = 0; v <= top; v += step) ticks.push(v)

  // Label roughly every 8th week — enough to orient, few enough to read.
  const labelEvery = Math.max(1, Math.ceil(rows.length / 8))

  return (
    <svg className='ns-chart' viewBox={`0 0 ${W} ${H}`} role='img'
      aria-label={`${data.metric} index by channel, ${rows.length} weeks`}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke='#222a44' strokeWidth={1} />
          <text x={PAD.left - 8} y={y(v) + 4} fill='#9aa6c7' fontSize={11} textAnchor='end'>{v}</text>
        </g>
      ))}
      {/* Baseline = 100 by construction, so it is the line the whole chart is read against. */}
      <line x1={PAD.left} x2={W - PAD.right} y1={y(100)} y2={y(100)}
        stroke='#6366f1' strokeWidth={1} strokeDasharray='4 4' opacity={0.8} />

      {rows.map((row, i) => {
        const x = PAD.left + i * slot + (slot - barW) / 2
        const known = data.channels.filter((c) => row.band[c.key] != null)

        // No breakdown for this week (an early web-only row, or one where every
        // fetch failed): draw the total in a neutral fill rather than guessing
        // a split. It is honest about being unattributed and still shows level.
        if (!known.length) {
          return (
            <rect key={row.week} x={x} y={y(row.index)} width={barW}
              height={Math.max(0, y(0) - y(row.index))} fill={UNATTRIBUTED_FILL}>
              <title>{`${row.week} · index ${row.index.toFixed(0)} · no channel breakdown`}</title>
            </rect>
          )
        }

        let acc = 0
        return (
          <g key={row.week}>
            {known.map((c) => {
              const v = row.band[c.key] as number
              const yTop = y(acc + v)
              const h = Math.max(0, y(acc) - y(acc + v))
              acc += v
              return (
                <rect key={c.key} x={x} y={yTop} width={barW} height={h} fill={c.color}>
                  <title>
                    {`${row.week} · ${c.label} ${v.toFixed(1)} of ${row.index.toFixed(0)}` +
                      ` (${(row.share[c.key] ?? 0).toFixed(1)}%)`}
                  </title>
                </rect>
              )
            })}
          </g>
        )
      })}

      {rows.map((row, i) =>
        i % labelEvery === 0 ? (
          <text key={row.week} x={PAD.left + i * slot + slot / 2} y={H - 6}
            fill='#9aa6c7' fontSize={10} textAnchor='middle'>
            {row.week.slice(5)}
          </text>
        ) : null,
      )}
    </svg>
  )
}

export default NorthStarChart
